import { useCallback, useEffect, useRef } from 'react'
import Cookies from 'js-cookie'
import { loginWithToken, verifyToken } from '../../util/auth/login'
import { setStore } from '../../util/store/auth'
import log from '../../util/logging'
import handleError from '../../util/handleError'
import logout from '../../util/auth/logout'

const REFRESH_TOKEN_THRESHOLD = 1000 * 60 * 60 // 1 hour
const CHECK_INTERVAL = 1000 * 60 * 5 // Check every 5 minutes

const checkIsTokenValid = async (): Promise<boolean> => {
  try {
    const { verified } = await verifyToken()
    return verified
  } catch (err) {
    log.error('Token refresh manager: Failed to verify token', err)
    return false
  }
}

const TokenRefreshManager = () => {
  const refreshingRef = useRef(false)

  const checkAndRefreshToken = useCallback(async () => {
    // Prevent concurrent refreshes
    if (refreshingRef.current) return

    const expiresAt = Cookies.get('expiresAt')
    // if no expiresAt, we're not logged in, so we don't need to refresh the token
    if (!expiresAt) return

    const expiresAtDate = new Date(parseInt(expiresAt) || 0)
    const now = new Date()

    // If token will expire within threshold, refresh it
    if (expiresAtDate.getTime() - now.getTime() < REFRESH_TOKEN_THRESHOLD) {
      refreshingRef.current = true

      try {
        const verified = await checkIsTokenValid()
        if (!verified) {
          log.info('Token refresh manager: Token already expired, logging out')
          await logout()
          return
        }

        log.info('Token refresh manager: Refreshing token...')
        const { user } = await loginWithToken()

        setStore({ user })
        log.info('Token refresh manager: Token refreshed successfully')
      } catch (err) {
        handleError(err, 'Token refresh manager: Failed to refresh token')
      } finally {
        refreshingRef.current = false
      }
    }
  }, [])

  useEffect(() => {
    // Check immediately on mount
    checkAndRefreshToken().catch(handleError)

    // Set up interval
    const interval = setInterval(() => {
      checkAndRefreshToken().catch(handleError)
    }, CHECK_INTERVAL)

    // Also check on visibility change (user returns to tab)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkAndRefreshToken().catch(handleError)
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [checkAndRefreshToken])

  return null // This component doesn't render anything
}

export default TokenRefreshManager
